import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CircleNotch, FloppyDisk, Lightning } from "@phosphor-icons/react";
import { EmergencySettingsCard } from "@/components/emergency/EmergencySettingsCard";
import { Button } from "@/components/ui/button";
import { apiGet, apiSend } from "@/lib/api-error";
import {
  EMPTY_SERVICE_BASELINE,
  loadServiceSavedBaseline,
  serviceSaveGrossPrice,
  type ServiceSavedBaseline,
} from "@/lib/service-price-save";
import { usePricingConfig } from "@/hooks/usePricingConfig";
import { toast } from "@/lib/toast";
import { formatValidationError } from "@/lib/validation-errors";
import { updateEmergencySettingsSchema } from "@/schemas/emergency";
import { SectionShell } from "./section-primitives";
import type { EmergencySettingsResponse } from "./types";

export function EmergencySection() {
  const queryClient = useQueryClient();
  const pricingConfig = usePricingConfig();
  const emergencyQuery = useQuery({
    queryKey: ["emergency-settings"],
    queryFn: () =>
      apiGet<EmergencySettingsResponse>(
        "/api/me/therapist/emergency-settings",
        "Greška pri učitavanju hitnih postavki",
      ),
  });
  const [enabled, setEnabled] = useState(false);
  const [price, setPrice] = useState<number | null>(null);
  // Sirovi spremljeni bruto uz prikazanu cijenu bez PDV-a: bruto -> bez PDV-a -> bruto
  // nije idempotentno za cijene koje nije proizveo kalkulator (admin, WP migracija), pa
  // bi samo uključivanje/isključivanje hitnih termina pomjerilo cijenu za paru.
  const [priceBaseline, setPriceBaseline] = useState<ServiceSavedBaseline>(EMPTY_SERVICE_BASELINE);

  // Depend on the rate primitives, not the config object: a window-focus refetch
  // hands back a new object with the same values and would otherwise reset any price
  // the therapist is mid-typing.
  const { vatRate, feePercent } = pricingConfig.config ?? {};
  useEffect(() => {
    if (!emergencyQuery.data || vatRate === undefined || feePercent === undefined) return;
    setEnabled(emergencyQuery.data.enabled);
    const baseline = loadServiceSavedBaseline(emergencyQuery.data.price, { vatRate, feePercent });
    setPrice(baseline.base);
    setPriceBaseline(baseline);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emergencyQuery.data, vatRate, feePercent]);

  const update = useMutation({
    mutationFn: (body: { enabled: boolean; price?: number }) =>
      apiSend("/api/me/therapist/emergency-settings", "PATCH", body, "Greška pri spremanju hitnih postavki"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["emergency-settings"] });
      toast.success("Hitne konsultacije su sačuvane.");
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Greška pri spremanju hitnih postavki"),
  });

  function handleSave() {
    // Isključeno: cijena se ne šalje ni ne provjerava, spremljena ostaje za kasnije.
    if (!enabled) {
      update.mutate({ enabled: false });
      return;
    }
    if (price === null || price <= 0) {
      toast.error("Unesi cijenu hitne konsultacije veću od 0 ili isključi opciju.");
      return;
    }
    const config = pricingConfig.config;
    if (!config) {
      toast.error("Cijene se još učitavaju, pokušajte ponovo za par sekundi.");
      return;
    }
    // Spremljena cijena je bruto (sa PDV-om), kao i kod usluga; provjerava je ista shema kao na serveru.
    const body = { enabled: true, price: serviceSaveGrossPrice(price, priceBaseline, config) };
    const parsed = updateEmergencySettingsSchema.safeParse(body);
    if (!parsed.success) {
      toast.error(formatValidationError(parsed.error.issues, body).error);
      return;
    }
    update.mutate(body);
  }

  return (
    <SectionShell
      icon={Lightning}
      title="Konfiguracija hitnih termina"
      description="Klijenti rezervišu i plaćaju hitne termine direktno, u blokovima koje označite u rasporedu."
      footer={
        <div className="flex flex-col items-start gap-1.5">
          <Button onClick={handleSave} disabled={update.isPending || (enabled && !pricingConfig.config)}>
            {update.isPending ? <CircleNotch className="size-4 animate-spin" /> : <FloppyDisk className="size-4" />}
            Sačuvaj hitne konsultacije
          </Button>
          {!pricingConfig.config && (
            <p className="text-xs text-muted-foreground">
              {pricingConfig.isError
                ? "Trenutno nije moguće učitati konfiguraciju cijena. Osvježite stranicu i pokušajte ponovo."
                : "Cijene se učitavaju."}
            </p>
          )}
        </div>
      }
    >
      <EmergencySettingsCard
        enabled={enabled}
        price={price}
        onEnabledChange={setEnabled}
        onPriceChange={setPrice}
        priceBaseline={priceBaseline}
      />
    </SectionShell>
  );
}
